/**
 * Holds Chrome's `beforeinstallprompt` event so the app can offer its own install button.
 *
 * A module-level store rather than a hook or a provider, because **the event is not
 * replayed**: Chrome fires it once, early, and a listener attached inside a component
 * effect would miss an event that fires while the login page is still mounting — the
 * Android install button would then silently never appear.
 *
 * `captureInstallPrompt()` is therefore called from `src/index.tsx`, at module scope.
 */
/** Not in lib.dom yet. */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISSED_KEY = "pwa-install-dismissed";

export type InstallPromptSnapshot = {
  /** Chrome has handed us a prompt we can fire. Always false on iOS. */
  canPrompt: boolean;
  /** The user closed the banner. The Settings entry ignores this. */
  isDismissed: boolean;
};

let deferredEvent: BeforeInstallPromptEvent | null = null;
let snapshot: InstallPromptSnapshot = { canPrompt: false, isDismissed: false };
let listeners: Array<() => void> = [];
let captured = false;

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === "true";
  } catch {
    // Safari in private mode throws on localStorage. Not being able to remember a
    // dismissal is better than not rendering.
    return false;
  }
}

function emit(next: InstallPromptSnapshot): void {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

export function subscribe(listener: () => void): () => void {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((entry) => entry !== listener);
  };
}

export function getSnapshot(): InstallPromptSnapshot {
  return snapshot;
}

/** Call once, at module scope, before React renders. */
export function captureInstallPrompt(): void {
  if (captured || typeof window === "undefined") return;
  captured = true;

  snapshot = { canPrompt: false, isDismissed: readDismissed() };

  window.addEventListener("beforeinstallprompt", (event) => {
    // Deliberately NO preventDefault: Chrome's own install promotion stays on. It fires
    // this event under heuristics we cannot see (and mutes it for a long time after a
    // dismissal), so suppressing its UI made our banner the single point of failure —
    // when Chrome stayed quiet, no install affordance existed anywhere. Now Chrome
    // promotes natively and the stashed event merely powers our banner and menu button.
    deferredEvent = event as BeforeInstallPromptEvent;
    emit({ ...snapshot, canPrompt: true });
  });

  window.addEventListener("appinstalled", () => {
    deferredEvent = null;
    emit({ ...snapshot, canPrompt: false });
  });
}

/**
 * Fires the native install dialog. Resolves to whether the user accepted.
 *
 * A deferred prompt is single-use — calling `prompt()` twice throws — so the event is
 * dropped either way.
 */
export async function promptInstall(): Promise<boolean> {
  const event = deferredEvent;
  if (!event) return false;
  deferredEvent = null;
  emit({ ...snapshot, canPrompt: false });
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome === "accepted";
  } catch {
    return false;
  }
}

export function dismiss(): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, "true");
  } catch {
    // Nothing to do — the banner just comes back next session.
  }
  emit({ ...snapshot, isDismissed: true });
}

/**
 * Clears the singleton between tests.
 *
 * A seam rather than `jest.isolateModules`: re-importing this module would also
 * re-import React for anything that consumes it, and a hook rendered against a second
 * React instance finds a null dispatcher.
 */
export function resetInstallPromptForTests(): void {
  deferredEvent = null;
  snapshot = { canPrompt: false, isDismissed: false };
  listeners = [];
  captured = false;
}
